// Shared delivery-pincode persistence.
//
// One shopper-entered pincode, remembered across pages: a check on the PDP
// pre-fills the next PDP, and — once the cart drawer's per-line delivery rows
// land — lets the cart resolve delivery dates without asking for it again.
//
// localStorage rather than sessionStorage: a delivery address does not change
// between tabs or visits the way a login session does, and re-typing it every
// visit is the thing this exists to remove.
//
// Every access is wrapped. localStorage throws outright in Safari private mode
// and wherever cookies are blocked, and a remembered pincode is a convenience —
// it must never be the reason the rest of a page's scripts stop running.
//
// Six digits matches `pincode_length` in snippets/pincode-check.liquid; the
// store is India-only, so the length is fixed rather than configurable.
const PINCODE_PATTERN = /^\d{6}$/;
const PINCODE_STORAGE_KEY = "theme:delivery-pincode";

// In-memory tier, and it is load-bearing rather than a nicety. Checkout is held
// closed until a pincode is known, so a shopper whose localStorage throws — Safari
// private mode, blocked cookies — would otherwise be stuck forever: the write
// silently fails, the read keeps answering "", and entering a pincode changes
// nothing. Holding it here lets that shopper finish the session; only persistence
// across navigation is lost.
let pincodeInMemory = "";

const PincodeStorage = {
  // Exposed so a listener on the `storage` event can recognise our key without
  // keeping a second copy of the string.
  key: PINCODE_STORAGE_KEY,

  // The remembered pincode, or "" when absent, unreadable, or no longer the shape
  // we store. A value left by an earlier build must read as "no pincode" rather
  // than reach a request malformed.
  get() {
    try {
      const stored = localStorage.getItem(PINCODE_STORAGE_KEY) ?? "";
      if (this.isValid(stored)) return stored;
    } catch {
      // Storage unreadable — fall through to the in-memory copy.
    }
    return this.isValid(pincodeInMemory) ? pincodeInMemory : "";
  },

  // Silently ignores anything that is not a well-formed pincode, so a caller never
  // has to guard the call site. Memory is written first and unconditionally: it is
  // the tier that cannot fail.
  set(pincode) {
    if (!this.isValid(pincode)) return;
    pincodeInMemory = pincode;
    try {
      localStorage.setItem(PINCODE_STORAGE_KEY, pincode);
    } catch {
      // Storage full or blocked. This session still has it; it simply stops
      // surviving navigation.
    }
  },

  clear() {
    pincodeInMemory = "";
    try {
      localStorage.removeItem(PINCODE_STORAGE_KEY);
    } catch {
      /* storage unavailable */
    }
  },

  isValid(pincode) {
    return PINCODE_PATTERN.test(pincode ?? "");
  },
};

window.PincodeStorage = PincodeStorage;
